// #3272 - "Check for updates" plumbing: ask the browser to fetch the newest
// sw.js NOW, and if that leaves a new worker waiting, learn which build it is.
//
// No fetch/XHR here (#1912): the page cannot read the new build's id off the
// network, so the waiting worker reports it itself over a MessageChannel. The
// answering side is generated by swBuildVersion.js (workbox importScripts);
// swUpdateCheck.test.js runs this file against that real script.
//
// Result of checkForUpdate():
//   { status: "upToDate" }
//   { status: "available", newBuildId }   newBuildId is null when the waiting
//                                         worker predates this feature and
//                                         never answers GET_VERSION.
// It REJECTS when the check cannot be made (no service worker support or
// registration, offline / update() failure, an install that never finishes).
// The caller shows that as "could not check", never as "up to date".

export const DEFAULT_INSTALL_TIMEOUT_MS = 30000;
export const DEFAULT_VERSION_TIMEOUT_MS = 3000;

function waitForInstalled(worker, timeoutMs) {
  return new Promise((resolve, reject) => {
    const done = (fn, arg) => {
      clearTimeout(timer);
      worker.removeEventListener("statechange", onState);
      fn(arg);
    };
    const onState = () => {
      if (worker.state === "installed") done(resolve);
      else if (worker.state === "redundant") {
        done(reject, new Error("The new version failed to install."));
      }
    };
    const timer = setTimeout(
      () => done(reject, new Error("Timed out installing the new version.")),
      timeoutMs,
    );
    worker.addEventListener("statechange", onState);
    onState();
  });
}

// Asks a worker for its build id. Resolves null (not rejects) when it does not
// answer in time: an unanswerable worker is still a real pending update.
export function askWorkerBuildId(worker, timeoutMs = DEFAULT_VERSION_TIMEOUT_MS) {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => {
      channel.port1.close();
      resolve(null);
    }, timeoutMs);
    channel.port1.onmessage = (event) => {
      if (event.data?.type !== "VERSION") return;
      clearTimeout(timer);
      channel.port1.close();
      resolve(event.data.buildId ?? null);
    };
    worker.postMessage({ type: "GET_VERSION" }, [channel.port2]);
  });
}

export async function checkForUpdate({
  installTimeoutMs = DEFAULT_INSTALL_TIMEOUT_MS,
  versionTimeoutMs = DEFAULT_VERSION_TIMEOUT_MS,
} = {}) {
  const container = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  if (!container) throw new Error("Service workers are not available.");
  const registration = await container.getRegistration();
  if (!registration) throw new Error("No service worker is registered.");

  await registration.update();

  if (!registration.waiting && registration.installing) {
    await waitForInstalled(registration.installing, installTimeoutMs);
  }
  const waiting = registration.waiting;
  if (!waiting) return { status: "upToDate" };
  return {
    status: "available",
    newBuildId: await askWorkerBuildId(waiting, versionTimeoutMs),
  };
}
